import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardApiKeysExpiring} from '@features/admin-panel/models/admin-dashboard-licences.models';
import {
  AdminDashboardApiKeyExpiring,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The card's title, on screen and in the PDF report (OCA-E-159 DetailSections:340). */
const TITLE = 'API Gateway keys expiring';
/** A key due within this many days is amber (OCA-E-159 DetailSections:353). */
const SOON_DAYS = 7;
/** The list shows at most this many keys (OCA-E-159 DetailSections:347); the subtitle counts them all. */
const MAX_LISTED = 5;
/** The headline and the empty text when nothing expires or lapsed. */
const NONE = 'No keys expire in the next 30 days or lapsed in the last 14.';

/**
 * API Gateway keys expiring: the in-scope companies' API Gateway access keys expiring in the next 30 days or lapsed
 * in the last 14 (ITP-E-160-07), lapsed first, as a compact list of at most five — the company (a Company 360 link),
 * the masked key and its APIs in monospace, and when it expires (amber within a week) or lapsed (red). The subtitle
 * counts every key. Computed the same way in both scopes. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-api-keys-expiring',
  templateUrl: './admin-dashboard-api-keys-expiring.component.html',
  styleUrls: ['./admin-dashboard-api-keys-expiring.component.scss']
})
export class AdminDashboardApiKeysExpiringComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly failedTitle = 'Couldn\'t load API Gateway keys expiring';
  rows: AdminDashboardApiKeyExpiring[] = [];
  /** The server's LapsedCount / ExpiringCount; null while it sends none (an older API). */
  lapsedCount: number | null = null;
  expiringCount: number | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private loadSubscription: Subscription;

  constructor(
    private dataService: AdminDashboardDataService,
    private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>,
    private router: Router
  ) {}

  /** The keys on screen: the first five, lapsed first (the server's order). */
  get listed(): AdminDashboardApiKeyExpiring[] {
    return this.rows.slice(0, MAX_LISTED);
  }

  /**
   * The count headline (OCA-E-159 DetailSections:341, the verb agreeing with the count): "2 keys expire in the next
   * 30 days; 1 lapsed in the last 14." / "1 key expires …". Without the server's counts, the R4 sentence.
   */
  get headline(): string {
    if (!this.loaded || this.failed) {
      return 'Keys expiring in the next 30 days or lapsed in the last 14.';
    }
    if (this.lapsedCount === null || this.expiringCount === null) {
      const count = this.rows.length;
      if (!count) {
        return 'No keys expire in the next 30 days.';
      }
      return count === 1 ? '1 key expires in the next 30 days.' : `${countOf(count, 'key')} expire in the next 30 days.`;
    }
    if (!this.lapsedCount && !this.expiringCount) {
      return NONE;
    }
    const expiring = this.expiringCount === 1 ? '1 key expires' : `${countOf(this.expiringCount, 'key')} expire`;
    return `${expiring} in the next 30 days; ${formatNumber(this.lapsedCount, 'en-US', '1.0-0')} lapsed in the last 14.`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('licences-revenue', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** "•••• a1b2 · FX, CR" (Q3): the masked key and the APIs, whichever the server sent; null with neither. */
  keyLine(row: AdminDashboardApiKeyExpiring): string | null {
    const parts = [row?.keyLabel, row?.apis].filter(part => !!part);
    return parts.length ? parts.join(' · ') : null;
  }

  /** The APIs part after the monospace masked key: " · FX, CR"; "FX, CR" without a key label; '' without APIs (Q3). */
  apisText(row: AdminDashboardApiKeyExpiring): string {
    if (!row?.apis) {
      return '';
    }
    return row.keyLabel ? ` · ${row.apis}` : row.apis;
  }

  /** "lapsed 13 days ago", "expires today", "in 1 day", "in 25 days" (OCA-E-159 DetailSections:354). */
  whenText(daysLeft: number): string {
    if (daysLeft < 0) {
      return `lapsed ${countOf(-daysLeft, 'day')} ago`;
    }
    return daysLeft === 0 ? 'expires today' : `in ${countOf(daysLeft, 'day')}`;
  }

  /** Lapsed: shown red. */
  isLapsed(daysLeft: number): boolean {
    return daysLeft < 0;
  }

  /** Due today or within a week: shown amber. */
  isSoon(daysLeft: number): boolean {
    return daysLeft >= 0 && daysLeft <= SOON_DAYS;
  }

  /** The company name: its Company 360 page. */
  open360(companyId: string): void {
    this.router.navigateByUrl('/admin-panel/company-360/' + companyId);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-10). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = TITLE;
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = this.headline;
    if (!this.rows.length) {
      return [{kind: 'empty', title, subtitle, text: 'No keys expiring'}];
    }
    return [{
      kind: 'table',
      title,
      subtitle,
      columns: [
        {header: 'Company', weight: 2}, {header: 'Key', weight: 2}, {header: 'Issued to', weight: 2},
        {header: 'Expires'}, {header: 'Days left', align: 'right'}
      ],
      // The red stripe marks lapsed keys and the amber one keys due within a week, like the colours on screen.
      rows: this.rows.map((row): AdminDashboardReportRow => {
        const reportRow: AdminDashboardReportRow = {
          cells: [
            row.companyName, this.keyLine(row) ?? '—', row.issuedTo || '—',
            formatDate(row.expiryDate, 'd MMM', 'en-US', 'UTC'), String(row.daysLeft)
          ]
        };
        if (this.isLapsed(row.daysLeft)) {
          reportRow.tone = 'red';
        } else if (this.isSoon(row.daysLeft)) {
          reportRow.tone = 'amber';
        }
        return reportRow;
      })
    }];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getWithExtra<AdminDashboardApiKeysExpiring>(
      'AdminDashboardApiKeysExpiringQuery', this.params, {}
    ).subscribe({
      next: response => this.show(response, false),
      error: () => this.show({Rows: []} as AdminDashboardApiKeysExpiring, true)
    });
  }

  private show(response: AdminDashboardApiKeysExpiring | null, failed: boolean): void {
    this.rows = Array.isArray(response?.Rows) ? response.Rows : [];
    this.lapsedCount = typeof response?.LapsedCount === 'number' ? response.LapsedCount : null;
    this.expiringCount = typeof response?.ExpiringCount === 'number' ? response.ExpiringCount : null;
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : this.rows.length ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
